import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Loader, CheckCircle, Clock, AlertCircle, 
  Calendar, RefreshCw, ChevronDown, ChevronUp, Star
} from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';
import { formatCurrency } from '../../utils/helpers';
import ChangePlanModal from './ChangePlanModal';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import BillingHistoryTable from './BillingHistoryTable';
import UpdatePaymentMethodModal from './UpdatePaymentMethodModal';
import PlanCard from './PlanCard';
import InvoiceModal from './InvoiceModal';

const SubscriptionTab = ({ showToast }) => {
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Plans state
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [currentBillingPeriod, setCurrentBillingPeriod] = useState(null);
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState(null);
  
  // Modal states
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);
  const [selectedPlanForChange, setSelectedPlanForChange] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Expand/collapse state for mobile
  const [showPlans, setShowPlans] = useState(true);

  // Founder flag from subscriptionInfo (populated by backend)
  const isFoundingMember = subscriptionInfo?.isFoundingMember === true;

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  // If a founder somehow has annual selected, force it to monthly.
  // Founder pricing is monthly-only (per ToS locked-rate commitment).
  useEffect(() => {
    if (isFoundingMember && billingPeriod === 'annual') {
      setBillingPeriod('monthly');
    }
  }, [isFoundingMember, billingPeriod]);

  const loadSubscriptionInfo = async () => {
    try {
      setSubscriptionLoading(true);
      const info = await subscriptionService.getStatus();
      setSubscriptionInfo(info);
      
      // Set current billing period from API response
      if (info.billingPeriod) {
        const period = info.billingPeriod.toLowerCase();
        setCurrentBillingPeriod(period);
        setBillingPeriod(period); // Default the toggle to current period
      }
      
      // Always try to load plans so founders on trial can see the Change Plan section too
      await Promise.all([
        loadPlans(),
        loadPaymentMethod()
      ]);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      const plansData = await subscriptionService.getPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const loadPaymentMethod = async () => {
    try {
      const pm = await subscriptionService.getPaymentMethod();
      setPaymentMethod(pm);
    } catch (error) {
      console.error('Error loading payment method:', error);
    }
  };

  const handlePlanChangeSuccess = async (newPlan) => {
    await loadSubscriptionInfo();
    showToast?.(`Successfully changed to ${newPlan} plan!`);
  };

  const handleCancelSuccess = async (immediate) => {
    await loadSubscriptionInfo();
    if (immediate) {
      showToast?.('Subscription cancelled immediately');
    } else {
      showToast?.('Subscription will cancel at the end of your billing period');
    }
  };

  const handlePaymentMethodUpdateSuccess = async () => {
    await loadPaymentMethod();
    showToast?.('Payment method updated successfully!');
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      Active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      Trial: { color: 'bg-blue-100 text-blue-800', label: 'Trial' },
      PastDue: { color: 'bg-yellow-100 text-yellow-800', label: 'Past Due' },
      Cancelled: { color: 'bg-red-100 text-red-800', label: 'Canceled' },
      Incomplete: { color: 'bg-gray-100 text-gray-800', label: 'Incomplete' },
      Expired: { color: 'bg-red-100 text-red-800', label: 'Expired' },
    };
    
    const badge = badges[status] || badges.Incomplete;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getBillingPeriodLabel = (period) => {
    if (!period) return '';
    return period.toLowerCase() === 'annual' ? 'Annual' : 'Monthly';
  };

  if (subscriptionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600">Loading subscription...</span>
      </div>
    );
  }

  const hasActivePlan = !!subscriptionInfo?.subscriptionPlan;
  const showChangePlanSection = hasActivePlan || subscriptionInfo?.subscriptionStatus === 'Trial';

  return (
    <div className="space-y-6">

      {/* Founding Member Banner - shown at very top when flagged */}
      {isFoundingMember && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="bg-amber-500 rounded-full p-2 flex-shrink-0">
              <Star className="h-5 w-5 text-white fill-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-bold text-amber-900 mb-1">
                You're a Founding Member
              </h3>
              <p className="text-sm text-amber-800">
                Your Pro plan is locked at <strong>$10/month for life</strong>, per your founding member agreement.
                This rate will never increase as long as your account remains active.
              </p>
              <p className="text-xs text-amber-700 mt-2">
                Founder pricing is monthly-only. Solo and Enterprise tiers are available at standard pricing if you need them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Card */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Subscription</h2>
        
        {subscriptionInfo ? (
          <div className="space-y-4">
            {/* Status, Plan, and Billing Period */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                {getStatusBadge(subscriptionInfo.subscriptionStatus)}
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Plan</p>
                <p className="text-lg font-semibold text-gray-900">
                  {subscriptionInfo.subscriptionPlan || (
                    <span className="text-sm font-normal text-gray-500 italic">No active plan</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Billing</p>
                <p className="text-lg font-semibold text-gray-900">
                  {getBillingPeriodLabel(currentBillingPeriod) || (
                    <span className="text-sm font-normal text-gray-500 italic">—</span>
                  )}
                </p>
              </div>
            </div>

            {/* Trial Banner */}
            {subscriptionInfo.subscriptionStatus === 'Trial' && (
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900 mb-1">
                      Free Trial Active
                    </h3>
                    <p className="text-sm text-blue-800">
                      Your trial ends on {formatDate(subscriptionInfo.trialEndDate)}. 
                      You have {subscriptionInfo.trialDaysRemaining} days remaining.
                    </p>
                    {!isFoundingMember && (
                      <a href="/subscribe" className="inline-block mt-2 text-sm font-medium text-blue-600 hover:text-blue-700">
                        Subscribe Now →
                      </a>
                    )}
                    {isFoundingMember && (
                      <p className="mt-2 text-sm text-blue-800">
                        <strong>Ready to activate?</strong> Choose Pro below to subscribe at your locked founder rate of $10/month.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Active Subscription Info */}
            {subscriptionInfo.subscriptionStatus === 'Active' && !subscriptionInfo.isCancelling && hasActivePlan && (
              <>
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-green-900 mb-1">
                        Subscription Active
                      </h3>
                      <p className="text-sm text-green-800">
                        Your next billing date is {formatDate(subscriptionInfo.currentPeriodEnd)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Payment Method</p>
                      {paymentMethod ? (
                        <p className="text-sm font-medium text-gray-900">
                          {paymentMethod.brand?.charAt(0).toUpperCase() + paymentMethod.brand?.slice(1)} 
                          {' '}•••• {paymentMethod.last4} 
                          {' '}(Exp {paymentMethod.expMonth}/{paymentMethod.expYear})
                        </p>
                      ) : (
                        <p className="text-sm text-orange-600 font-medium">
                          No payment method on file - Add one to avoid service interruption
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowUpdatePaymentModal(true)}
                    className={`text-sm font-medium ${paymentMethod ? 'text-blue-600 hover:text-blue-700' : 'text-orange-600 hover:text-orange-700'}`}
                  >
                    {paymentMethod ? 'Update' : 'Add Card'}
                  </button>
                </div>
              </>
            )}

            {/* No plan state - shown to Active accounts without a Stripe subscription (e.g. platform admins) */}
            {subscriptionInfo.subscriptionStatus === 'Active' && !hasActivePlan && (
              <div className="bg-gray-50 border-l-4 border-gray-300 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-gray-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      No subscription plan on record
                    </h3>
                    <p className="text-sm text-gray-700">
                      Your account is active but not tied to a paid subscription plan.
                      If you believe this is unexpected, please contact support.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Cancelling Banner */}
            {subscriptionInfo.isCancelling && (
              <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-orange-900 mb-1">
                      Subscription Ending
                    </h3>
                    <p className="text-sm text-orange-800">
                      Your subscription will end on {formatDate(subscriptionInfo.subscriptionCancelAt)}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Past Due Banner */}
            {subscriptionInfo.subscriptionStatus === 'PastDue' && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                      Payment Issue
                    </h3>
                    <p className="text-sm text-yellow-800 mb-2">
                      Your last payment failed. Please update your payment method.
                    </p>
                    <button
                      onClick={() => setShowUpdatePaymentModal(true)}
                      className="text-sm font-medium text-yellow-900 hover:text-yellow-800 underline"
                    >
                      Update Payment Method
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No active subscription found</p>
            <a href="/subscribe" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
              <CreditCard className="mr-2 h-5 w-5" />
              View Plans
            </a>
          </div>
        )}
      </div>

      {/* Change Plan Section - Show for Active plans AND Trial users (so founders can subscribe) */}
      {showChangePlanSection && !subscriptionInfo?.isCancelling && (
        <div className="card">
          {/* Header with toggle for mobile */}
          <div 
            className="flex items-center justify-between cursor-pointer sm:cursor-default"
            onClick={() => setShowPlans(!showPlans)}
          >
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {subscriptionInfo?.subscriptionStatus === 'Trial' ? 'Choose Your Plan' : 'Change Plan'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {subscriptionInfo?.subscriptionStatus === 'Trial'
                  ? 'Activate your subscription to continue after your trial ends'
                  : 'Upgrade, downgrade, or switch billing period'}
              </p>
            </div>
            
            {/* Mobile expand/collapse indicator */}
            <button className="sm:hidden p-2 text-gray-400">
              {showPlans ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          {/* Billing Period Toggle - Hidden entirely for founders (monthly-only per agreement) */}
          <div className={`mt-4 ${showPlans ? 'block' : 'hidden sm:block'}`}>
            {!isFoundingMember && (
              <div className="flex justify-center sm:justify-end mb-4">
                <div className="bg-gray-100 rounded-lg p-1 inline-flex">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                      billingPeriod === 'monthly' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    className={`px-4 py-2 rounded-md font-medium text-sm transition-colors relative ${
                      billingPeriod === 'annual' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Annual
                    <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      Save
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Plan Cards */}
            {plansLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
            ) : plans.length > 0 ? (
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${showPlans ? 'block' : 'hidden sm:grid'}`}>
                {plans.map((plan) => {
                  const isSamePlan = subscriptionInfo?.subscriptionPlan === plan.name ||
                    (subscriptionInfo?.subscriptionPlan === 'Professional' && plan.name === 'Pro') ||
                    (subscriptionInfo?.subscriptionPlan === 'Pro' && plan.name === 'Professional') ||
                    (subscriptionInfo?.subscriptionPlan === 'Starter' && plan.name === 'Solo') ||
                    (subscriptionInfo?.subscriptionPlan === 'Solo' && plan.name === 'Starter');
                  
                  const isSameBillingPeriod = currentBillingPeriod === billingPeriod;
                  const isCurrentPlan = isSamePlan && isSameBillingPeriod;
                  const isCurrentPlanDifferentPeriod = isSamePlan && !isSameBillingPeriod;
                  
                  return (
                    <PlanCard
                      key={plan.name}
                      plan={plan}
                      billingPeriod={billingPeriod}
                      currentBillingPeriod={currentBillingPeriod}
                      isCurrentPlan={isCurrentPlan}
                      isCurrentPlanDifferentPeriod={isCurrentPlanDifferentPeriod}
                      isFoundingMember={isFoundingMember}
                      onSelectPlan={(selectedPlan) => {
                        setSelectedPlanForChange(selectedPlan);
                        setShowChangePlanModal(true);
                      }}
                      disabled={false}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No plans available</p>
            )}
          </div>
        </div>
      )}

      {/* Billing History - only show for users with an actual Stripe customer record */}
      {hasActivePlan && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing History</h2>
          <p className="text-gray-600 mb-6">View and download your past invoices</p>
          <BillingHistoryTable onViewInvoice={handleViewInvoice} />
        </div>
      )}

      {/* Cancel Subscription */}
      {subscriptionInfo && subscriptionInfo.subscriptionStatus === 'Active' && !subscriptionInfo.isCancelling && hasActivePlan && (
        <div className="card border-red-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cancel Subscription</h2>
          <p className="text-gray-600 mb-4">
            Need to cancel? Your data will be preserved for 30 days after cancellation.
          </p>
          <button
            onClick={() => setShowCancelModal(true)}
            className="px-6 py-3 border-2 border-red-300 text-red-700 font-semibold rounded-lg hover:bg-red-50 transition-colors"
          >
            Cancel Subscription
          </button>
        </div>
      )}

      {/* Modals */}
      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => {
          setShowChangePlanModal(false);
          setSelectedPlanForChange(null);
        }}
        currentPlan={subscriptionInfo?.subscriptionPlan}
        currentBillingPeriod={currentBillingPeriod}
        targetPlan={selectedPlanForChange}
        billingPeriod={billingPeriod}
        isFoundingMember={isFoundingMember}
        hasActiveSubscription={!!subscriptionInfo?.stripeSubscriptionId}
        onSuccess={handlePlanChangeSuccess}
      />

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        currentPlan={subscriptionInfo?.subscriptionPlan}
        onSuccess={handleCancelSuccess}
      />

      <UpdatePaymentMethodModal
        isOpen={showUpdatePaymentModal}
        onClose={() => setShowUpdatePaymentModal(false)}
        onSuccess={handlePaymentMethodUpdateSuccess}
        currentCard={paymentMethod}
      />

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />
    </div>
  );
};

export default SubscriptionTab;